import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

// 86d3fcbte — drag-resizable, persisted width for the pinned Description column
// in the review-mode line-items lists (Quotation + Sales Order share this).
//
// The width is exposed as a CSS custom property (`--review-desc-w`) set on the
// scroll container; every Description cell reads `width: var(--review-desc-w)`.
// During a drag we write the variable straight to the DOM node (no React state
// churn — important with dozens of rows) and only commit to state + localStorage
// on release. Reload restores the last width.
const STORAGE_KEY = 'dop.review.descColWidth';
const DEFAULT_WIDTH = 256; // matches the previous fixed w-64
const MIN_WIDTH = 140;
const MAX_WIDTH = 560;

export const REVIEW_DESC_CSS_VAR = '--review-desc-w';

function clampWidth(n: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(n)));
}

function readStored(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const n = raw != null ? Number(raw) : NaN;
    return Number.isFinite(n) ? clampWidth(n) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

export function useReviewDescWidth() {
  const [width, setWidth] = useState<number>(readStored);
  // Attach to the scroll container (<ol>) — the live CSS var is written here
  // during a drag so cells update without a React render.
  const containerRef = useRef<HTMLOListElement | null>(null);

  const commit = useCallback((next: number) => {
    const w = clampWidth(next);
    setWidth(w);
    try {
      localStorage.setItem(STORAGE_KEY, String(w));
    } catch {
      /* ignore quota / privacy-mode failures */
    }
  }, []);

  const startResize = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      e.preventDefault();
      const handle = e.currentTarget;
      const container = containerRef.current;
      // Pointer capture keeps move/up events flowing to the handle even when the
      // cursor passes over the embedded PDF <object> in the source pane.
      handle.setPointerCapture(e.pointerId);

      const startX = e.clientX;
      const startW = width;
      let latest = startW;

      const onMove = (ev: PointerEvent) => {
        latest = clampWidth(startW + (ev.clientX - startX));
        container?.style.setProperty(REVIEW_DESC_CSS_VAR, `${latest}px`);
      };
      const stop = (ev: PointerEvent) => {
        handle.releasePointerCapture?.(ev.pointerId);
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', stop);
        handle.removeEventListener('pointercancel', stop);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        commit(latest);
      };

      handle.addEventListener('pointermove', onMove);
      handle.addEventListener('pointerup', stop);
      handle.addEventListener('pointercancel', stop);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [width, commit],
  );

  return { width, containerRef, startResize };
}
